<template>
  <router-link
    v-if="href && useVueRouter"
    :to="href"
    :target="openInNewTab? '_blank': '_self'"
    v-bind="$attrs"
    class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition tw-duration-150 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
    v-bind:class="buttonClasses"
    :disabled="disabled"
  >
    <font-awesome-icon v-if="enableLoading" icon="circle-notch" spin class="tw-h-4 tw-w-4" />
    <font-awesome-icon v-else-if="icon" :icon="icon" class="tw-h-4 tw-w-4" />
    <slot></slot>
  </router-link>
  <a
    v-else-if="href"
    :href="href"
    :target="openInNewTab? '_blank': '_self'"
    v-bind="$attrs"
    class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition tw-duration-150 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
    v-bind:class="buttonClasses"
    :disabled="disabled"
  >
    <font-awesome-icon v-if="enableLoading" icon="circle-notch" spin class="tw-h-4 tw-w-4" />
    <font-awesome-icon v-else-if="icon" :icon="icon" class="tw-h-4 tw-w-4" />
    <slot></slot>
  </a>
  <button
    v-else
    v-on:click="$emit('click', $event)"
    v-bind="$attrs"
    class="tw-inline-flex tw-items-center tw-justify-center tw-gap-2 tw-rounded-lg tw-border tw-px-3 tw-py-2 tw-text-sm tw-font-medium tw-transition tw-duration-150 disabled:tw-opacity-60 disabled:tw-cursor-not-allowed"
    v-bind:class="buttonClasses"
    :disabled="disabled"
  >
    <font-awesome-icon v-if="enableLoading" icon="circle-notch" spin class="tw-h-4 tw-w-4" />
    <font-awesome-icon v-else-if="icon" :icon="icon" class="tw-h-4 tw-w-4" />
    <slot></slot>
  </button>
</template>

<script lang="ts">
import { computed, defineComponent } from 'vue';

export default defineComponent({
  inheritAttrs: false,
  emits: ['click'], 
  props: {
    buttonStyle: {
      default: 'default',
    },
    isPrimary: {
      default: true,
    },
    isSecondary: {
      default: false,
    },
    isGray: {
      default: false,
    },
    isGreen: {
      default: false,
    },
    isRed: {
      default: false,
    },
    isDarkRed: {
      default: false,
    },
    isDefault: {
      default: false,
    },
    isMediumText: {
      default: false,
    },
    isLargeText: {
      default: false,
    },
    enableLoading: {
      default: false,
    },
    href: {
      default: undefined,
    },
    useVueRouter: {
      default: true,
    },
    openInNewTab: {
      default: false,
    },
    disabled: {
      default: false,
    },
    icon: {
      default: undefined,
    },
  },
  computed: {
    buttonClasses() {

      let bgClass = '';
      let bgOnHoverClass = '';
      let borderClass = '';
      let textColorClass = '';
      let textOnHoverClass = '';

      // DEFAULT === SECONDARY (Why ??!)
      // light grey/slate border, white 60% opacity background (100% on hover), dark grey text
      if (this.isDefault || this.isSecondary) {
        bgClass = 'tw-bg-white/60'
        bgOnHoverClass = 'hover:tw-bg-white'
        borderClass = 'tw-border-slate-200'
        textColorClass = 'tw-text-slate-800'
      } 
      if (this.isGray || this.isRed || this.isGreen || this.isDarkRed) {
        // GRAY: 
        // light grey border, grey bg (heavier grey on hover), white text, 
        if (this.isGray) {
          bgClass = 'tw-bg-slate-600'
          bgOnHoverClass = 'hover:tw-bg-slate-700'
          borderClass = 'tw-border-slate-200'
          textColorClass = 'tw-text-white'
        }
        
        // GREEN / (DARK)RED
        // transparent border, white text
        else if (this.isGreen || this.isRed || this.isDarkRed) {
          borderClass = 'tw-border-transparent'
          textColorClass = 'tw-text-white'

          if (this.isRed) {
            // color X bg (heavier color bg on hover),
            // lighter for isSecondary
            if (!this.isSecondary) {
              bgClass = 'tw-bg-rose-600'
              bgOnHoverClass = 'hover:tw-bg-rose-700'
            } else {
              bgClass = 'tw-bg-rose-400'
              bgOnHoverClass = 'hover:tw-bg-rose-500'
            }
          } else if (this.isGreen) {
            // color X bg (heavier color bg on hover),
            // lighter for isSecondary
            if (!this.isSecondary) {
              bgClass = 'tw-bg-emerald-600'
              bgOnHoverClass = 'hover:tw-bg-emerald-700'
            } else {
              bgClass = 'tw-bg-emerald-400'
              bgOnHoverClass = 'hover:tw-bg-emerald-500'
            }
          } else if (this.isDarkRed) {
            bgClass = 'tw-bg-rose-900'
            bgOnHoverClass = 'hover:tw-bg-rose-950'
          }
        }
      
      } else if (this.isPrimary) {
        // if PRIMARY (and not secondary/default and NOT a color: gray/red/green/dark red)
        // transparent border, dark grey background (lighter on hover), white text
        bgClass = 'tw-bg-slate-900'
        bgOnHoverClass = 'hover:tw-bg-slate-800'
        borderClass = 'tw-border-transparent'
        textColorClass = 'tw-text-white'
      }
      let textSizeClass = '';
      if (this.isMediumText) {
        textSizeClass = 'tw-text-base';
      } else if (this.isLargeText) {
        textSizeClass = 'tw-text-lg';
      } 
      return [
        bgClass,
        bgOnHoverClass,
        borderClass,
        textColorClass,
        textSizeClass,
      ];
    },
  },
});
</script>